import { Item } from "../utils/workspaceItemUtils";
import { getRootLevelItems, getFolderItems } from "../utils/workspaceItemUtils";
import {
  collectExistingFolderPaths,
  findFolderById,
  findFolderIdByPath,
} from "../utils/folderUtils";
import Workspace from "@/models/workspace/Workspace";
import { logger } from "@/lib/logger";
import { DownloadedFileWithIds } from "../providers/SharePointProvider";
import {
  FolderConflictResult,
  FileWithRelativePath,
} from "../types/upload.types";

/**
 * Gets items from a specific folder or root level
 */
export const getItemsFromFolder = (
  workspace: Workspace,
  folderId?: string
): Item[] => {
  if (!folderId) {
    return getRootLevelItems(workspace);
  }
  const folder = findFolderById(workspace, folderId);
  if (!folder) return [];
  return getFolderItems(folder, []);
};

/**
 * Gets a map of workspace file names (lowercase) to their document IDs
 * Used for checking conflicts and finding existing documents for revisions
 */
export const getWorkspaceFileNames = (
  currentItems: Item[],
  workspace?: Workspace,
  folderId?: string
): Record<string, string> => {
  // Defensive check for null/undefined
  if (!currentItems || !Array.isArray(currentItems)) {
    logger.warn("Workspace items are not a list; name conflict check skipped", {
      operation: "getWorkspaceFileNames",
      workspaceId: workspace?.id,
      folderId,
    });
    return {} as Record<string, string>;
  }

  // If workspace and folderId are provided, filter items by folder first
  let itemsToCheck = currentItems;
  if (workspace !== undefined && folderId !== undefined) {
    const folderItems = getItemsFromFolder(workspace, folderId);
    // Filter currentItems to only include items from the specified folder
    const folderItemIds = new Set(folderItems.map((item) => item.id));
    itemsToCheck = currentItems.filter((item) => folderItemIds.has(item.id));
  }

  return itemsToCheck
    .filter((item) => item.type === "document")
    .reduce(
      (acc, item) => {
        // Use lowercase key to match how we look up in handleUploadFiles
        acc[item.name.toLowerCase()] = item.id;
        return acc;
      },
      {} as Record<string, string>
    );
};

/**
 * Checks for file name conflicts between uploaded files and existing workspace items
 */
export const checkForFileNameConflicts = (
  files: FileList,
  currentItems: Item[],
  workspace?: Workspace,
  folderId?: string
): File[] => {
  const conflicts: File[] = [];

  // Defensive check for null/undefined
  if (!currentItems || !Array.isArray(currentItems)) {
    logger.warn("Workspace items are not a list; name conflict check skipped", {
      operation: "checkForFileNameConflicts",
      workspaceId: workspace?.id,
      folderId,
    });
    return [];
  }

  if (!files) {
    logger.warn("No files to check for name conflicts", {
      operation: "checkForFileNameConflicts",
      workspaceId: workspace?.id,
      folderId,
    });
    return [];
  }

  // Filter items by folder if workspace and folderId are provided
  let itemsToCheck = currentItems;
  if (workspace !== undefined && folderId !== undefined) {
    const folderItems = getItemsFromFolder(workspace, folderId);
    const folderItemIds = new Set(folderItems.map((item) => item.id));
    itemsToCheck = currentItems.filter((item) => folderItemIds.has(item.id));
  }

  const existingFileNames = itemsToCheck
    .filter((item) => item.type === "document")
    .map((item) => item.name.toLowerCase());

  Array.from(files).forEach((file) => {
    if (existingFileNames.includes(file.name.toLowerCase())) {
      conflicts.push(file);
    }
  });

  return conflicts;
};

/**
 * Checks for file name conflicts from downloaded SharePoint/OneDrive files
 */
export const checkForFileNameConflictsFromDownloaded = (
  downloadedFiles: DownloadedFileWithIds[],
  currentItems: Item[],
  workspace?: Workspace,
  folderId?: string
): DownloadedFileWithIds[] => {
  const conflicts: DownloadedFileWithIds[] = [];

  // Defensive check for null/undefined
  if (!currentItems || !Array.isArray(currentItems)) {
    logger.warn("Workspace items are not a list; name conflict check skipped", {
      operation: "checkForFileNameConflictsFromDownloaded",
      workspaceId: workspace?.id,
      folderId,
    });
    return [];
  }

  if (!downloadedFiles || !Array.isArray(downloadedFiles)) {
    logger.warn(
      "Downloaded files are not a list; name conflict check skipped",
      {
        operation: "checkForFileNameConflictsFromDownloaded",
        workspaceId: workspace?.id,
        folderId,
      }
    );
    return [];
  }

  // Filter items by folder if workspace and folderId are provided
  let itemsToCheck = currentItems;
  if (workspace !== undefined && folderId !== undefined) {
    const folderItems = getItemsFromFolder(workspace, folderId);
    const folderItemIds = new Set(folderItems.map((item) => item.id));
    itemsToCheck = currentItems.filter((item) => folderItemIds.has(item.id));
  }

  const existingFileNames = itemsToCheck
    .filter((item) => item.type === "document")
    .map((item) => item.name.toLowerCase());

  downloadedFiles.forEach((fileWithIds) => {
    if (existingFileNames.includes(fileWithIds.file.name.toLowerCase())) {
      conflicts.push(fileWithIds);
    }
  });

  return conflicts;
};

/**
 * Checks for folder conflicts and returns existing vs new folder paths
 * @param folderPaths - Array of folder paths to check
 * @param workspace - The workspace to check against
 * @param parentFolderId - Optional parent folder ID to check within
 * @returns Object with existing paths set and new paths array
 */
export const checkForFolderConflicts = (
  folderPaths: string[],
  workspace: Workspace,
  parentFolderId?: string
): FolderConflictResult => {
  const currentFolder = findFolderById(workspace, parentFolderId);
  const foldersToCheck = currentFolder
    ? currentFolder.folders
    : workspace.folders;

  const existingFolderPaths = collectExistingFolderPaths(foldersToCheck);
  const existingPaths = new Set<string>();
  const newPaths: string[] = [];

  folderPaths.forEach((path) => {
    const pathLower = path.toLowerCase();
    const exists = Array.from(existingFolderPaths).some(
      (existingPath) => existingPath.toLowerCase() === pathLower
    );
    if (exists) {
      existingPaths.add(path);
    } else {
      newPaths.push(path);
    }
  });

  return { existingPaths, newPaths };
};

/**
 * Checks for file name conflicts within folders during folder upload
 * @param files - Array of files to check
 * @param workspace - The workspace to check against
 * @param parentFolderId - Optional parent folder ID to check within
 * @param _existingFolderPaths - Set of existing folder paths (unused but kept for API compatibility)
 * @returns Array of conflicting files
 */
export const checkForFileNameConflictsInFolders = (
  files: File[],
  workspace: Workspace,
  parentFolderId?: string,
  _existingFolderPaths?: Set<string>
): File[] => {
  const conflicts: File[] = [];

  files.forEach((file) => {
    const relativePath =
      (file as FileWithRelativePath).webkitRelativePath || file.name;
    const pathParts = relativePath.split("/");
    const fileName = pathParts[pathParts.length - 1];
    const folderPath =
      pathParts.length > 1 ? pathParts.slice(0, -1).join("/") : undefined;

    // Find target folder ID - check if folder exists first
    let targetFolderId: string | undefined = undefined;
    if (folderPath) {
      // Try to find the folder in workspace
      targetFolderId = findFolderIdByPath(
        workspace,
        folderPath,
        parentFolderId
      );

      // If folder doesn't exist yet (will be created), there can't be a conflict
      // because it's a new folder with no existing files
      if (targetFolderId === undefined) {
        return; // Skip this file, no conflict possible in a new folder
      }
    } else {
      // Root level file
      targetFolderId = parentFolderId;
    }

    // Get items from the target folder only
    const folderItems = getItemsFromFolder(workspace, targetFolderId);

    // Check if file name conflicts with documents in the same folder
    const hasConflict = folderItems.some(
      (item) =>
        item.type === "document" &&
        item.name.toLowerCase() === fileName.toLowerCase()
    );

    if (hasConflict) {
      conflicts.push(file);
    }
  });

  return conflicts;
};

/**
 * Extracts folder paths from files with webkitRelativePath
 */
export const extractFolderPathsFromFiles = (files: File[]): string[] => {
  const folderPathsSet = new Set<string>();

  files.forEach((file) => {
    const relativePath =
      (file as FileWithRelativePath).webkitRelativePath || file.name;
    const pathParts = relativePath.split("/").filter(Boolean);

    // Extract all parent folder paths
    for (let i = 1; i < pathParts.length; i++) {
      const folderPath = pathParts.slice(0, i).join("/");
      if (folderPath && folderPath.trim().length > 0) {
        folderPathsSet.add(folderPath);
      }
    }
  });

  return Array.from(folderPathsSet);
};
